/* Mounts (feature:mount), client side: pick a mount per army, combine the stats, draw rider + mount.
   Rider and mount are two instances with a shared position: the army's own meshes draw the rider, the mount gets one
   extra instanced mesh per LOD (outlines come from the screen pass), so it stays one draw call per army, LOD and body. The rider sits on the
   saddle via its instance matrix; aAnim.x < 0 switches its legs to a seated pose in the vertex shader.
   Sim side: src/sim/features/mount.js (slot states 0 = on foot, 1 = riding, 2 = riderless). */
import * as THREE from 'three';
import { DEC, TYPES, buildArmy } from '../game/voxel.js';

// fit: the mount is scaled up until it is at least fit x the rider's height; seatZ: saddle along the body
// (fraction of the half length, + = towards the head); hitRider: share of hits that land on the rider
export const MOUNTS = {
  huhn: { name: 'Huhn', fit: 1, seatZ: -0.15, hitRider: 0.55 },
  trex: { name: 'T-Rex', fit: 1.4, seatZ: -0.08, hitRider: 0.3 },
  mechhase: { name: 'Mech-Hase', fit: 1.2, seatZ: 0, hitRider: 0.35 },
  panzer: { name: 'Panzer', fit: 0.7, seatZ: -0.1, hitRider: 0.3 },
  seemonster: { name: 'Seemonster', fit: 2.2, seatZ: 0.25, hitRider: 0.15 },
};
const FOOT = 0, RIDING = 1, FREE = 2;
const COARSE = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
const H1 = {}; // model height of each mount at size 1 (the model does not depend on the size)

/* ---------------- stats ---------------- */
// saddle on the model's back: highest voxel of the centre column at seatZ; metres at size 1, origin = feet centre
function seatOf(B, zf) {
  const m = B.m, c = B.ctr; let z0 = 1e9, z1 = -1e9, top = -1e9;
  for (const k of m.map.keys()) { const z = DEC(k)[2]; if (z < z0) z0 = z; if (z > z1) z1 = z; }
  const zs = c.cz + zf * (z1 + 1 - z0) / 2;
  for (const k of m.map.keys()) { const [x, y, z] = DEC(k); if (Math.abs(x + 0.5 - c.cx) <= 1.5 && Math.abs(z + 0.5 - zs) <= 1.5 && y + 1 > top) top = y + 1; }
  if (top < -1e8) top = c.y0 + c.h * 0.7;
  return { y: (top - c.y0) * m.vs, z: (zs - c.cz) * m.vs };
}
// rider hip (leg pivot) height in metres at size 1; 0 = sits on its base (vehicles, blobs)
function hipOf(A) { const k = A.roles.indexOf(4); return k >= 0 ? Math.max(0, A.pivots[k].y) : 0; }

/** After buildArmy: give armies with a chosen mount combined stats (sent to the worker inside stats.mount). */
export function mountArmies(armies, cfgs) {
  for (let g = 0; g < armies.length; g++) {
    const A = armies[g], cfg = cfgs[g], t = A.team ? 1 : 0;
    // a group without its own choice: the side's "Reittier" select (classic panel), only for its first troop
    if (cfg.mount === undefined) cfg.mount = (!cfg.slot && typeof document !== 'undefined' && document.getElementById('mount' + t)?.value) || '';
    A.mount = null;
    const key = cfg.mount, MK = MOUNTS[key], ty = TYPES[key];
    if (!MK || !ty) continue;
    const rider = A.stats, base = { type: key, count: rider.count, weapon: ty.weapon, t: 0.4, width: 1, head: 1 };
    if (!(key in H1)) H1[key] = buildArmy(t, { ...base, size: 1 }).h;
    const msz = Math.min(60, Math.max(1, MK.fit * rider.h / H1[key]));
    const B = buildArmy(t, { ...base, size: msz }), S = B.stats;
    const seat = seatOf(B, MK.seatZ), hip = hipOf(A);
    const top = seat.y * msz - hip * rider.s; // rider origin above the mount's origin
    A.stats = {
      ...rider, speed: S.speed, r: S.r, mass: S.mass + rider.mass, stride: S.stride, turn: S.turn, giant: S.giant,
      hp: rider.hp + S.hp, h: top + rider.h, my: top + rider.my, mz: rider.mz + seat.z * msz,
      mount: {
        key, hp: S.hp, hitRider: MK.hitRider, seat: top, foot: rider, free: S, flee: S.bravery < 0.5,
        stomp: !!ty.stomp, stompR: S.r * 1.5 + 1.2, stompDmg: S.dmg * 0.5, stompKnock: Math.max(S.knock, 6), stompCd: 2,
      },
    };
    A.mount = { key, B, msz, seatY: seat.y, seatZ: seat.z, hip, st: new Uint8Array(0) };
  }
}

/* ---------------- meshes ---------------- */
// seated pose for riders: aAnim.x < 0 => walk 0, legs forward and spread around the mount's back
export const seatPatch = vs => vs
  .replace('float walk = min(aAnim.x, 1.6);', 'float seat = step(aAnim.x, -0.5); float walk = max(min(aAnim.x, 1.6), 0.0);')
  .replace('a += s*0.7*walk; R = rX(a);', 'a += s*0.7*walk; R = rZ(sign(piv.x)*0.55*seat) * rX(a - seat*0.75);')
  .replace('a += -s*0.7*walk; R = rX(a);', 'a += -s*0.7*walk; R = rZ(sign(piv.x)*0.55*seat) * rX(a - seat*0.75);');
function patchSeat(mats) {
  for (const k of ['mat', 'mat0', 'depth']) {
    const m = mats[k], f = m.onBeforeCompile, key = m.customProgramCacheKey();
    m.onBeforeCompile = (sh, r) => { f(sh, r); sh.vertexShader = seatPatch(sh.vertexShader); };
    m.customProgramCacheKey = () => key + '-mount';
  }
}
function dropMeshes(M, scene) {
  if (!M.lod1) return;
  for (const m of [M.lod0, M.lod1]) { scene.remove(m); m.geometry.dispose(); m.dispose(); }
  M.mats.mat.dispose(); M.mats.mat0.dispose(); M.mats.depth.dispose();
  M.lod0 = M.lod1 = null;
}
/** End of unitMeshes(A): seated rider materials + the mount's own instanced meshes (same capacity as the rider's). */
export function mountMeshes(A, ctx) {
  const M = A.mount, Q = ctx.Q, scene = ctx.scene;
  dropMeshes(M, scene);
  patchSeat(A.mats);
  const B = M.B, cap1 = A.lod1.instanceMatrix.count, g0 = B.geo0;
  const tris = (g0.index ? g0.index.count : g0.attributes.position.count) / 3;
  const cap0 = Math.min(cap1, Q.lod0, Math.max(8, Math.floor(Q.lod0Tris / Math.max(1, tris))));
  M.mats = ctx.makeUnitMaterial(B);
  const mk = (geo, cap, mat) => {
    const g = geo.clone();
    const anim = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); anim.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('aAnim', anim);
    const im = new THREE.InstancedMesh(g, mat, cap); im.instanceMatrix.setUsage(THREE.DynamicDrawUsage); im.frustumCulled = false;
    im.customDepthMaterial = M.mats.depth; im.castShadow = Q.shadows > 0; im.receiveShadow = Q.shadows > 0; im.count = 0;
    return im;
  };
  M.lod0 = mk(B.geo0, cap0, M.mats.mat0); M.lod1 = mk(B.geo1, cap1, M.mats.mat); // near LOD: greedy mesh + voxel jitter
  scene.add(M.lod0, M.lod1);
  const st = new Uint8Array(cap1).fill(RIDING); st.set(M.st.subarray(0, Math.min(cap1, M.st.length))); M.st = st;
}
/** Before the armies are rebuilt. */
export function mountDispose(armies, ctx) { for (const A of armies) if (A && A.mount) dropMeshes(A.mount, ctx.scene); }

/** Slot states from the worker ('mount' message, posted only when one changed). */
export function mountSync(armies, st) {
  for (const A of armies) {
    const M = A && A.mount; if (!M) continue;
    const off = A.start | 0, n = Math.min(M.st.length, st.length - off);
    if (n > 0) M.st.set(st.subarray(off, off + n));
  }
}
/** Body that breaks apart when slot i dies: the mount's voxels once the rider is gone. */
export function mountBody(A, i) {
  const M = A.mount; if (!M) return A;
  return M.st[i - (A.start | 0)] === FREE ? M.B : A;
}

/* ---------------- render ---------------- */
function put(M, k, c, sn, cp, sp, s, x, y, z) {
  const m = k * 16;
  M[m] = c * s; M[m + 1] = 0; M[m + 2] = -sn * s; M[m + 3] = 0;
  M[m + 4] = sn * sp * s; M[m + 5] = cp * s; M[m + 6] = c * sp * s; M[m + 7] = 0;
  M[m + 8] = sn * cp * s; M[m + 9] = -sp * s; M[m + 10] = c * cp * s; M[m + 11] = 0;
  M[m + 12] = x; M[m + 13] = y; M[m + 14] = z; M[m + 15] = 1;
}
const anim = (An, k, a, b, c, d) => { const o = k * 4; An[o] = a; An[o + 1] = b; An[o + 2] = c; An[o + 3] = d; };
const bobOf = (B, s) => B.m.kind === 'humanoid' ? B.h * 0.035 * s : B.m.kind === 'vehicle' ? 0 : B.h * 0.02 * s;
const lerpAng = (a, b, t) => { let d = b - a; while (d > Math.PI) d -= 6.2832; while (d < -Math.PI) d += 6.2832; return a + d * t; };
function upload(mesh, n) {
  if (!n) return;
  mesh.instanceMatrix.clearUpdateRanges(); mesh.instanceMatrix.addUpdateRange(0, n * 16); mesh.instanceMatrix.needsUpdate = true;
  const an = mesh.geometry.getAttribute('aAnim'); an.clearUpdateRanges(); an.addUpdateRange(0, n * 4); an.needsUpdate = true;
}
/** Replaces renderUnits' per-unit loop for a mounted army: mount at the unit, rider on its saddle (or on foot). */
export function mountRender(A, t, alpha, pb, ctx) {
  const M = A.mount, B = M.B, curB = ctx.curB, st = M.st, Q = ctx.Q, rState = ctx.rState, possessed = ctx.possessed;
  const start = A.start | 0, end = Math.min(A.end, ctx.N);
  const cam = ctx.camera.position, cx = cam.x, cy = cam.y, cz = cam.z;
  const s0 = A.stats.s, big = Math.max(s0, M.msz);
  const d0 = Q.lod0Dist * Math.max(1, Math.sqrt(big) * (B.h * M.msz > 2 ? 2 : 1)), d02 = d0 * d0;
  const r0 = A.lod0.instanceMatrix.array, r1 = A.lod1.instanceMatrix.array, ra0 = A.lod0.geometry.getAttribute('aAnim').array, ra1 = A.lod1.geometry.getAttribute('aAnim').array;
  const q0 = M.lod0.instanceMatrix.array, q1 = M.lod1.instanceMatrix.array, qa0 = M.lod0.geometry.getAttribute('aAnim').array, qa1 = M.lod1.geometry.getAttribute('aAnim').array;
  const cap0 = A.lod0.instanceMatrix.count, mcap0 = M.lod0.instanceMatrix.count;
  const bobR = bobOf(A, s0), bobM = bobOf(B, M.msz), leanR = A.m.kind === 'humanoid' ? 0.09 : 0.04;
  let n0 = 0, n1 = 0, m0 = 0, m1 = 0;
  for (let i = start; i < end; i++) {
    const o = i * 10; if (!curB[o + 9]) continue;
    const x = pb[o] + (curB[o] - pb[o]) * alpha, y = pb[o + 1] + (curB[o + 1] - pb[o + 1]) * alpha, z = pb[o + 2] + (curB[o + 2] - pb[o + 2]) * alpha;
    const yaw = lerpAng(pb[o + 3], curB[o + 3], alpha), tumble = pb[o + 4] + (curB[o + 4] - pb[o + 4]) * alpha;
    const walk = pb[o + 5] + (curB[o + 5] - pb[o + 5]) * alpha, ph = pb[o + 6] + (curB[o + 6] - pb[o + 6]) * alpha;
    const atk = curB[o + 7], ex = lerpAng(pb[o + 8], curB[o + 8], alpha), air = curB[o + 9] > 1.5;
    const k = i - start, s = k < st.length ? st[k] : RIDING, rs = A.varS[k];
    const me = i === possessed, hideMe = me && ctx.fpv; // first person: the camera sits in this unit, don't draw it
    if (me) { rState.x = x; rState.y = y; rState.z = z; rState.g = air ? ctx.groundH(x, z) : y; }
    ctx.blob(x, air ? ctx.groundH(x, z) : y, z, A.stats.r, y);
    const dx = x - cx, dz = z - cz, near = dx * dx + dz * dz + cy * cy * 0.5 < d02 || i === possessed;
    const c = Math.cos(yaw), sn = Math.sin(yaw);
    if (s === FOOT) {
      if (me) { rState.eye = A.h * rs * 0.88; if (hideMe) continue; }
      const pitch = tumble + leanR * Math.min(walk, 1.6), cp = Math.cos(pitch), sp = Math.sin(pitch);
      const yy = y + (air ? 0 : Math.abs(Math.sin(ph)) * bobR * Math.min(walk, 1.5));
      const rl = near && n0 < cap0, kr = rl ? n0++ : n1++;
      put(rl ? r0 : r1, kr, c, sn, cp, sp, rs, x, yy, z); anim(rl ? ra0 : ra1, kr, walk, ph, atk, ex);
      continue;
    }
    const ms = rs / s0 * M.msz;
    const pitch = tumble + 0.04 * Math.min(walk, 1.6), cp = Math.cos(pitch), sp = Math.sin(pitch);
    const yy = y + (air ? 0 : Math.abs(Math.sin(ph)) * bobM * Math.min(walk, 1.5));
    if (me && s === FREE) { rState.eye = B.h * ms * 0.85; if (hideMe) continue; } // the rider fell: you are the animal
    const lo = near && m0 < mcap0, km = lo ? m0++ : m1++;
    put(lo ? q0 : q1, km, c, sn, cp, sp, ms, x, yy, z);
    if (s === FREE) { anim(lo ? qa0 : qa1, km, walk, ph, atk, ex); continue; }
    anim(lo ? qa0 : qa1, km, walk, ph, -1, 0);
    // rider: hips on the saddle, tilted with the mount; walk -1 = seated pose
    const up = M.seatY * ms - M.hip * rs, fw = M.seatZ * ms;
    if (me) { rState.eye = up + A.h * rs * 0.88; if (hideMe) continue; } // riding: eye above the saddle, the mount stays visible
    const rl = near && n0 < cap0, kr = rl ? n0++ : n1++;
    put(rl ? r0 : r1, kr, c, sn, cp, sp, rs, x + sn * sp * up + sn * cp * fw, yy + cp * up - sp * fw, z + c * sp * up + c * cp * fw);
    anim(rl ? ra0 : ra1, kr, -1, ph, atk, ex);
  }
  A.lod0.count = n0; A.lod1.count = n1; if (A.imp) A.imp.commit(0); // mounted armies skip the far impostors
  M.lod0.count = m0; M.lod1.count = m1;
  upload(A.lod0, n0); upload(A.lod1, n1); upload(M.lod0, m0); upload(M.lod1, m1);
}

/* ---------------- events -> FX ---------------- */
// voxels of body B (size s) fly apart at (x, y, z); same look as a unit's death
function burst(B, s, x, y, z, yaw, ctx) {
  const debris = ctx.debris, c = Math.cos(yaw), sn = Math.sin(yaw), rs = Math.sqrt(s);
  const n = Math.max(3, Math.round(B.debrisN * ctx.Q.debrisMul)), vsz = B.m.vs * s, gy = ctx.groundH(x, z);
  for (let k = 0; k < n; k++) {
    const vi = (Math.random() * B.voxN) | 0;
    const lx = B.vox[vi * 3] * s, ly = B.vox[vi * 3 + 1] * s, lz = B.vox[vi * 3 + 2] * s;
    const dl = Math.hypot(lx, lz) || 1, out = 1.5 + Math.random() * 3;
    const vx = (c * lx + sn * lz) / dl * out + (Math.random() - 0.5) * 2, vz = (-sn * lx + c * lz) / dl * out + (Math.random() - 0.5) * 2;
    debris.spawn(x + c * lx + sn * lz, y + ly, z - sn * lx + c * lz, vx * rs, (2 + Math.random() * 4.5) * rs, vz * rs, COARSE ? 9 : 16 + Math.random() * 8,
      vsz * (Math.random() < 0.25 ? 1.8 : 1) * (0.9 + Math.random() * 0.4), 18, 2 + Math.random() * 9, 0, B.vcol[vi * 3], B.vcol[vi * 3 + 1], B.vcol[vi * 3 + 2], gy);
  }
}
let dustT = 0, dustN = 0;
/** Worker events 13 (mount died), 14 (rider died), 15 (stomp). ev[o] = type, then the payload. */
export function mountEvent(type, ev, o, armies, ctx) {
  if (type === 15) {
    const now = performance.now(); if (now - dustT > 100) { dustT = now; dustN = 0; }
    if (++dustN > 24) return; // thousands of stomping T-Rex: a few dust rings per frame are enough
    const x = ev[o + 1], z = ev[o + 2], R = ev[o + 3], g = ctx.groundH(x, z);
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * 6.28, sp = R * (1.1 + Math.random() * 0.5);
      ctx.fx.spawn(x, g + 0.3, z, Math.sin(a) * sp, 0.6 + Math.random(), Math.cos(a) * sp, 0.5 + Math.random() * 0.3, 0.3 + R * 0.05, 0, 3, 2, 0.85, 0.78, 0.6);
    }
    return;
  }
  const i = ev[o + 1] | 0, A = armies[ev[o + 6] | 0], M = A && A.mount; if (!M) return; // the event carries the group
  if (type === 13) burst(M.B, M.msz, ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5], ctx);
  else burst(A, A.stats.s, ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5], ctx);
  if (i === ctx.possessed) ctx.toast(type === 13 ? 'Dein Reittier ist gefallen. Du kämpfst zu Fuß weiter.' : 'Dein Reiter ist gefallen. Du steuerst jetzt das Tier.');
}

/* ---------------- UI ---------------- */
/** "Reittier" select per army, right below the weapon. Crate-locked animals stay locked here too. */
export function mountUI(shop, onChange) {
  const opts = () => '<option value="">keins</option>' + shop.options('unit', Object.entries(MOUNTS));
  for (let t = 0; t < 2; t++) {
    const w = document.getElementById('weapon' + t); if (!w) continue;
    w.insertAdjacentHTML('afterend', `<label for="mount${t}">Reittier</label><select id="mount${t}" title="Jede Einheit reitet auf jedem Tier. Tier und Reiter haben eigene Lebenspunkte; fällt das Tier, kämpft der Reiter zu Fuß weiter.">${opts()}</select>`);
    const sel = document.getElementById('mount' + t);
    shop.guardSelect(sel, 'unit');
    sel.addEventListener('change', onChange);
  }
}
